import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Copy, Download, Volume2, Loader2, Upload, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from './LanguageSelector';
import { supabase } from '@/integrations/supabase/client';
import AccessGateModal from './AccessGateModal';
import type { AccessState } from '@/hooks/useMembershipStatus';
import { useLanguage } from '@/i18n/LanguageContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import OutputModeSelector, { type OutputMode } from './OutputModeSelector';

interface ImageTranslationProps {
  accessState: AccessState;
}

const ImageTranslation = ({ accessState }: ImageTranslationProps) => {
  const canTranslate = accessState === 'active' || accessState === 'trial';
  const [targetLanguage, setTargetLanguage] = useState<string>('en');
  const [extractedText, setExtractedText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showGateModal, setShowGateModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [outputMode, setOutputMode] = useState<OutputMode>('text');
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const translatorRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);
  const showResetControl = extractedText.trim().length > 0 || translatedText.trim().length > 0;
  const hasResettableContent = Boolean(imageFile || imagePreview || extractedText.trim() || translatedText.trim());
  const requiresResetConfirmation = Boolean(imageFile) || extractedText.trim().length >= 300 || translatedText.trim().length >= 300;

  const readFileAsBase64 = useCallback((file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('INVALID_IMAGE_DATA'));
        return;
      }
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(new Error('IMAGE_READ_FAILED'));
    reader.readAsDataURL(file);
  }), []);

  const handleResetNow = useCallback(() => {
    requestIdRef.current += 1;
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    if (fileInputRef.current) fileInputRef.current.value = '';
    setImagePreview(null);
    setImageFile(null);
    setExtractedText('');
    setTranslatedText('');
    setLoading(false);
    setTranslating(false);
    setSpeaking(false);
    setShowResetConfirm(false);
  }, []);

  const requestReset = useCallback(() => {
    if (!hasResettableContent) return;
    if (requiresResetConfirmation) {
      setShowResetConfirm(true);
      return;
    }
    handleResetNow();
  }, [handleResetNow, hasResettableContent, requiresResetConfirmation]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((!event.ctrlKey && !event.metaKey) || event.key.toLowerCase() !== 'n') return;
      const activeElement = document.activeElement;
      if (!translatorRef.current || !activeElement || !translatorRef.current.contains(activeElement)) return;
      event.preventDefault();
      requestReset();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [requestReset]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) { toast.error(t('image.pngJpgOnly')); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t('image.maxSize')); return; }
    requestIdRef.current += 1;
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setLoading(false);
    setTranslating(false);
    setSpeaking(false);
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
    setExtractedText(''); setTranslatedText('');
  };

  const handleExtractText = async () => {
    if (!canTranslate) { setShowGateModal(true); return; }
    if (!imageFile) { toast.error(t('image.uploadFirst')); return; }
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const base64Image = await readFileAsBase64(imageFile);
      const { data, error } = await supabase.functions.invoke('extract-text-from-image', { body: { image: base64Image } });
      if (currentRequestId !== requestIdRef.current) return;
      if (error) throw error;
      if (data?.error) {
        if (data.error === 'MEMBERSHIP_PENDING') toast.error(t('membership.pending'));
        else if (data.error === 'MEMBERSHIP_BLOCKED') toast.error(t('membership.blocked'));
        else throw new Error(data.error);
        return;
      }
      if (!data.extractedText || data.extractedText.trim() === '') { toast.error(t('image.noTextFound')); setExtractedText(''); }
      else { setExtractedText(data.extractedText); toast.success(t('image.textExtracted')); }
    } catch {
      if (currentRequestId === requestIdRef.current) toast.error(t('image.extractionFailed'));
    } finally {
      if (currentRequestId === requestIdRef.current) setLoading(false);
    }
  };

  const handleTranslate = async () => {
    if (!canTranslate) { setShowGateModal(true); return; }
    if (!extractedText.trim()) { toast.error(t('image.noTextToTranslate')); return; }
    const currentRequestId = ++requestIdRef.current;
    setTranslating(true);
    try {
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: { text: extractedText, sourceLanguage: 'auto-detect', targetLanguage, isImageTranslation: true },
      });
      if (currentRequestId !== requestIdRef.current) return;
      if (error) throw error;
      if (data?.error) {
        if (data.error === 'MEMBERSHIP_PENDING') toast.error(t('membership.pending'));
        else if (data.error === 'MEMBERSHIP_BLOCKED') toast.error(t('membership.blocked'));
        else throw new Error(data.error);
        return;
      }
      setTranslatedText(data.translatedText);
      toast.success(t('text.translationComplete'));
    } catch (error: any) {
      const errorMsg = error.message || error.error || JSON.stringify(error);
      if (errorMsg.toLowerCase().includes('trial')) toast.error(t('text.trialEnded'));
      else toast.error(t('text.somethingWentWrong'));
    } finally {
      if (currentRequestId === requestIdRef.current) setTranslating(false);
    }
  };

  const handleCopy = () => { navigator.clipboard.writeText(translatedText); toast.success(t('text.copiedToClipboard')); };
  const handleDownload = () => {
    const blob = new Blob([translatedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'translation.txt'; a.click(); URL.revokeObjectURL(url);
    toast.success(t('text.downloadedSuccessfully'));
  };
  const handleSpeak = () => {
    if ('speechSynthesis' in window) {
      setSpeaking(true); const utterance = new SpeechSynthesisUtterance(translatedText); utterance.lang = targetLanguage; utterance.onend = () => setSpeaking(false); speechSynthesis.speak(utterance);
    } else { toast.error(t('text.ttsNotSupported')); }
  };

  return (
    <div ref={translatorRef} className="rounded-xl border bg-card shadow-lg p-4 sm:p-6 space-y-4">
      <div className="rounded-lg bg-background border border-border/50 p-4 sm:p-5 space-y-4">
        <div>
          <Label className="text-xs sm:text-sm">{t('image.uploadImage')}</Label>
          <div className="border-2 border-dashed rounded-lg p-6 sm:p-8 text-center hover:border-primary/50 transition-colors">
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleImageUpload} className="hidden" id="image-upload" />
            <label htmlFor="image-upload" className="cursor-pointer">
              <Upload className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
              <p className="text-xs sm:text-sm text-muted-foreground mb-2">{t('image.clickToUpload')}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{t('image.fileTypes')}</p>
            </label>
          </div>
        </div>
        {imagePreview && (<div className="relative"><img src={imagePreview} alt="Preview" className="max-h-48 sm:max-h-64 mx-auto rounded-lg shadow-md" /></div>)}
        {imageFile && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleExtractText} disabled={loading || !canTranslate} className="flex-1 h-11 sm:h-10 text-sm hover:scale-[1.02] transition-transform">
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('image.extractingText')}</>) : t('image.extractText')}
            </Button>
            {!translatedText && showResetControl && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" onClick={requestReset} className="h-11 sm:h-10 px-4 text-sm gap-1.5 animate-in fade-in-0 zoom-in-95">
                      <RotateCcw className="h-4 w-4" />
                      {t('text.newTranslation')}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('text.newTranslationTooltip')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}

        <OutputModeSelector value={outputMode} onValueChange={setOutputMode} voiceOnlyDisabled={!translatedText.trim()} />
      </div>

      {extractedText && (
        <div className="grid md:grid-cols-2 gap-3 md:gap-0">
          <div className="rounded-lg md:rounded-r-none bg-background border border-border/50 p-4 sm:p-5 space-y-4 min-h-[280px] sm:min-h-[340px]">
            <div>
              <Label className="text-xs sm:text-sm">{t('image.extractedText')}</Label>
              <Textarea value={extractedText} onChange={(e) => setExtractedText(e.target.value)} className="min-h-[120px] sm:min-h-[150px] resize-none text-sm leading-relaxed" />
            </div>
            <div>
              <Label className="text-xs sm:text-sm">{t('text.targetLanguage')}</Label>
              <LanguageSelector value={targetLanguage} onValueChange={setTargetLanguage} />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleTranslate} disabled={translating || !canTranslate} className="flex-1 h-11 sm:h-10 text-sm hover:scale-[1.02] transition-transform">
                {translating ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('text.translating')}</>) : t('image.translateExtracted')}
              </Button>
              {!translatedText && showResetControl && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" onClick={requestReset} className="h-11 sm:h-10 px-4 text-sm gap-1.5 animate-in fade-in-0 zoom-in-95">
                        <RotateCcw className="h-4 w-4" />
                        {t('text.newTranslation')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('text.newTranslationTooltip')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {translatedText && (
            <div className="rounded-lg md:rounded-l-none bg-muted/10 border border-border p-4 sm:p-5 min-h-[280px] sm:min-h-[340px]">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <Label className="text-xs sm:text-sm">{t('text.translation')}</Label>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" onClick={requestReset} className="hidden md:inline-flex gap-1.5 text-xs h-8 px-3 animate-in fade-in-0 zoom-in-95">
                        <RotateCcw className="h-3.5 w-3.5" />
                        {t('text.newTranslation')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('text.newTranslationTooltip')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="space-y-2">
                <Textarea value={translatedText} readOnly className="min-h-[120px] sm:min-h-[150px] resize-none bg-muted/30 text-sm leading-relaxed" />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={requestReset} className="gap-1.5 text-xs h-9 sm:h-8 px-3 md:hidden animate-in fade-in-0 zoom-in-95">
                    <RotateCcw className="h-3.5 w-3.5" />
                    {t('text.newTranslation')}
                  </Button>
                  {outputMode !== 'voice' && <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5 text-xs h-9 sm:h-8 px-3 hover:scale-105 transition-transform">
                    <Copy className="h-3.5 w-3.5" />
                    {t('text.copy')}
                  </Button>}
                  <Button variant="outline" size="sm" onClick={handleSpeak} disabled={speaking} className="gap-1.5 text-xs h-9 sm:h-8 px-3 hover:scale-105 transition-transform">
                    <Volume2 className="h-3.5 w-3.5" />
                    {speaking ? t('text.speaking') : t('text.speak')}
                  </Button>
                  {outputMode !== 'voice' && <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5 text-xs h-9 sm:h-8 px-3 hover:scale-105 transition-transform">
                    <Download className="h-3.5 w-3.5" />
                    {t('text.download')}
                  </Button>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('text.resetConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('text.resetConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('text.keepEditing')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetNow}>{t('text.clearAll')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AccessGateModal open={showGateModal} onOpenChange={setShowGateModal} accessState={accessState} />
    </div>
  );
};

export default ImageTranslation;
